import React, { useCallback, useEffect, useState } from "react";
import { Button, Tooltip } from "antd";

import { controller as rootController } from "../../../controller";
import { useGetPatientInsuranceEligibility } from "./useGetPatientInsuranceEligibility";
import InsuranceMasterModal from "./InsuranceMasterModal";
import PatientPolicyInfoFormModal from "./PatientPolicyInfoFormModal";
import Eligibility2FAFormModal from "./Eligibility2FAFormModal";
import EligibilityDataModal from "./EligibilityDataModal";
import SelectPolicyItemModal from "./SelectPolicyItemModal";
import SikkaPolicyReviewModal from "../../InsuranceBreakDown/SikkaPolicyReviewModal";
import { useEnsurePolicies } from "./useEnsurePolicies";
import PolicyCompletionModal from "./shared/PolicyCompletionModal";
import { isPolicyComplete } from "../lib/policyCompleteness";

import agnetEditIcon from "../../../assets/icons/personal-card.svg";
import folder from "../../../assets/icons/folder-open-grey.svg";
import eyeGrey from "../../../assets/icons/eye-grey.svg";

function normalizePatientForModals(patient) {
  if (!patient) return null;
  return {
    ...patient,
    phone: patient.phone != null && patient.phone !== "" ? patient.phone : patient.phone_number,
  };
}

/**
 * Insurance policy + breakdown modals and actions for a patient row on the treatment-plans list.
 * Mirrors TreatmentCardStep2 insurance wiring so behavior matches the plan details page.
 */
export default function TreatmentPlanListRowInsuranceActions({
  patient,
  isTourDemo,
  onPatientInsuranceUpdated,
  // Headless reuse (e.g. from the Agent Runs drawer): hide the Policy/Insurance
  // buttons, auto-open the insurance modal on a given tab, and notify on close.
  hideTriggers = false,
  autoOpenInsuranceTab,
  // Pre-select a specific policy in the breakdown/estimate dropdown when the
  // modal is opened programmatically (e.g. from an FTC work-item chip).
  defaultSelectedPolicyId,
  // The agent's Details Needed suggestion ({ note, fields }) from that chip.
  outcomeSuggestion,
  // Pre-select a specific breakdown/estimate ROW inside that policy. Set by the
  // email deep-link landing, so a card's "Open in Smilepass" reaches the exact
  // record the FTC wrote about rather than merely the right patient. Unset
  // everywhere else, which leaves today's behaviour (segment-derived, then the
  // modal's own newest-entry default) untouched.
  defaultSelectedEntryId,
  // Deep-link request from a per-policy flag chip on the board row:
  // { policyId, tab, nonce }. A changing nonce opens the modal on that tab,
  // pre-selected to that policy.
  deepLinkRequest,
  // On the New Breakdowns / New Estimates tabs the row shows a single "View"
  // button that deep-links the modal to the freshly-fetched item.
  selectedSegment,
  onInsuranceModalClosed,
  // The patient's OWN office. Passed by cross-office surfaces (FTC Tasks) so the
  // insurance modal's office-scoped credential reads use the patient's office, not
  // the header selectedOffice. Omitted on single-office pages (falls back).
  officeId,
  // Open the insurance modal as a READER: no carrier contact bar, no office
  // claim-submission identity, no saved portal login, no Verify / Add Manually /
  // Upload-PDF rail. Set by the Insurance Requests page, whose users asked for
  // the verification rather than performing it. See InsuranceMasterModal.
  hideVerificationTools = false,
}) {
  const patientId = patient && (patient.id != null ? patient.id : patient.patient_id);
  const patientForModalsBase = normalizePatientForModals(
    patient && patient.id == null && patient.patient_id != null ? { ...patient, id: patient.patient_id } : patient,
  );

  // "View Breakdown" / "View Estimate" deep-link (New Breakdowns / New Estimates
  // tabs): open the modal on the matching tab with the freshly-fetched item's
  // policy + entry pre-selected. Ids come from the patient-list row payload
  // (latest_breakdown_* / latest_estimate_*); null ids degrade to the modal's
  // own defaults (first policy / newest entry).
  const isFetchedItemSegment = selectedSegment === "fresh-breakdowns" || selectedSegment === "new-estimates";
  const viewIsEstimate = selectedSegment === "new-estimates";
  const viewPolicyId = !isFetchedItemSegment
    ? undefined
    : viewIsEstimate
      ? patient && patient.latest_estimate_policy_id
      : patient && patient.latest_breakdown_policy_id;
  const viewEntryId = !isFetchedItemSegment
    ? undefined
    : viewIsEstimate
      ? patient && patient.latest_estimate_id
      : patient && patient.latest_breakdown_id;
  const effectiveDefaultPolicyId = defaultSelectedPolicyId != null ? defaultSelectedPolicyId : viewPolicyId;
  const effectiveDefaultEntryId = defaultSelectedEntryId != null ? defaultSelectedEntryId : viewEntryId;

  // Hold the attention note at the row level so an inline edit survives a
  // modal close/reopen (the modal uses destroyOnClose, so its own copy is
  // wiped — but this row stays mounted). Seeded from the patient prop and
  // re-synced if the parent later refreshes with newer data.
  const [attentionNote, setAttentionNote] = useState(
    patientForModalsBase ? patientForModalsBase.manual_attention_note : undefined,
  );
  useEffect(
    function () {
      setAttentionNote(patient ? patient.manual_attention_note : undefined);
    },
    [patient && patient.manual_attention_note],
  );
  const patientForModals = patientForModalsBase
    ? { ...patientForModalsBase, manual_attention_note: attentionNote }
    : patientForModalsBase;

  // Per-policy modal callback. The second arg is either a note STRING (the note
  // editors) or a PATCH OBJECT of per-policy fields (the outcome buttons —
  // breakdown_verified_at / needs_attention_at / …). Optimistically patch that
  // policy in the row's patientPolicies so the buttons, note header and chips
  // all re-derive from it (and it survives the modal's destroyOnClose).
  function handleModalNoteSaved(policyId, noteOrPatch) {
    if (typeof policyId !== "number") {
      setAttentionNote(typeof policyId === "string" ? policyId : noteOrPatch);
      return;
    }
    const patch = (noteOrPatch && typeof noteOrPatch === "object")
      ? noteOrPatch
      : { attention_note: noteOrPatch || null, attention_note_set_at: new Date().toISOString() };
    setPatientPolicies(function (prev) {
      return (prev || []).map(function (p) {
        return p.id === policyId ? { ...p, ...patch } : p;
      });
    });
    if (typeof onPatientInsuranceUpdated === "function") onPatientInsuranceUpdated(undefined, true);
  }

  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [activeInsuranceTab, setActiveInsuranceTab] = useState("breakdown");
  // When a board chip deep-links to a specific policy, this overrides the
  // modal's default policy selection (cleared on close so a normal reopen uses
  // the segment default).
  const [deepLinkPolicyId, setDeepLinkPolicyId] = useState(undefined);
  const [showPatientInsuranceFormModal, setShowPatientInsuranceFormModal] = useState(false);
  const [policyModalInitialMode, setPolicyModalInitialMode] = useState(undefined);
  const [showEligibility2FAFormModal, setShowEligibility2FAFormModal] = useState(false);
  const [showEligibilityDataModal, setShowEligibilityDataModal] = useState(false);
  const [cancelProcess, setCancelProcess] = useState(false);
  const [editPatientInsuranceData, setEditPatientInsuranceData] = useState(false);
  const [, setLastUploadedBreakdown] = useState(undefined);
  const [patientPolicies, setPatientPolicies] = useState([]);
  const [showSelectPolicyItemModal, setShowSelectPolicyItemModal] = useState(false);
  const [agentType, setAgentType] = useState(undefined);
  const [selectedPolicy, setSelectedPolicy] = useState(undefined);
  const [breakdownEligibilityRefreshTick, setBreakdownEligibilityRefreshTick] = useState(0);
  // Single-policy Instant AI Fetch blocked on missing critical data: hold the
  // policy + the agent type so we can resume the dispatch once it's completed.
  const [completionPolicy, setCompletionPolicy] = useState(undefined);
  const [pendingAgentType, setPendingAgentType] = useState(undefined);

  const {
    checkPatientInsuranceEligibility,
    isLoading: isAgentScrappingData,
    eligibilityData,
    breakdownData,
    predeterminationData,
    eligibilityAgentToken,
    breakdownModalEligibilityActive,
    agent2FAStatus,
    taskStatus: eligibilityTaskStatus,
  } = useGetPatientInsuranceEligibility(patientId, setShowPatientInsuranceFormModal, setShowEligibility2FAFormModal, cancelProcess, {
    onBreakdownEligibilityComplete: () => setBreakdownEligibilityRefreshTick((n) => n + 1),
  });

  const refreshPolicies = useCallback(
    async function refreshPoliciesInner() {
      if (!patientId) return [];
      try {
        const polRes = await rootController.getPatientPolicies(patientId);
        if (polRes.status < 250 && Array.isArray(polRes.json)) {
          setPatientPolicies(polRes.json);
          return polRes.json;
        }
      } catch (e) {
        console.error(e);
      }
      return [];
    },
    [patientId],
  );

  useEffect(
    function () {
      refreshPolicies();
    },
    [refreshPolicies],
  );

  // Auto-fetch the patient's PMS policies when they have none on Smilepass yet
  // (shared with TreatmentCard's card-level actions). Confident carriers save
  // silently; unknown carriers open the review modal below via reviewProps.
  const { ensurePolicies, refreshFromPms, isFetchingPolicies, reviewProps } = useEnsurePolicies(
    patientId,
    officeId,
    refreshPolicies,
  );

  // Opened programmatically (Agent Runs drawer): pop the insurance modal onto
  // the requested tab. The caller remounts this component per click, so a
  // mount-time open is sufficient.
  useEffect(
    function () {
      if (autoOpenInsuranceTab) {
        setActiveInsuranceTab(autoOpenInsuranceTab);
        setShowInsuranceModal(true);
      }
    },
    [autoOpenInsuranceTab],
  );

  // Board chip deep-link: open the modal on the requested tab pre-selected to
  // the flagged policy. Keyed on the nonce so an identical re-click retriggers.
  useEffect(
    function () {
      if (deepLinkRequest && deepLinkRequest.nonce) {
        setDeepLinkPolicyId(deepLinkRequest.policyId);
        setActiveInsuranceTab(deepLinkRequest.tab || "breakdown");
        setShowInsuranceModal(true);
      }
    },
    [deepLinkRequest && deepLinkRequest.nonce],
  );

  useEffect(
    function () {
      if (agentType === "eligibility") {
        if (eligibilityData === undefined) return;
      } else if (agentType === "breakdown") {
        if (breakdownData === undefined) return;
      } else if (agentType === "predetermination") {
        if (predeterminationData === undefined) return;
      }

      if (agentType === "eligibility") {
        setShowEligibilityDataModal(true);
      } else if (agentType === "breakdown") {
        setActiveInsuranceTab("breakdown");
        setShowInsuranceModal(true);
      } else if (agentType === "predetermination") {
        setActiveInsuranceTab("estimates");
        setShowInsuranceModal(true);
      }
    },
    [eligibilityData, breakdownData, predeterminationData, agentType],
  );

  async function handleReadData2() {
    await refreshPolicies();
    if (typeof onPatientInsuranceUpdated === "function") {
      onPatientInsuranceUpdated();
    }
  }

  // Used when a policy is added/edited from inside the breakdown/estimate
  // modal. Refreshes this row's policy dropdown AND the patient list, but the
  // list refresh is SILENT (getPatientsBySegment(undefined, true)) so it
  // doesn't flip the global loading flag — that would unmount the row and
  // close the open modal mid-save. (Non-silent handleReadData2 was closing
  // the modal + "refreshing the page".)
  async function handlePolicySaved() {
    await refreshPolicies();
    if (typeof onPatientInsuranceUpdated === "function") {
      onPatientInsuranceUpdated(undefined, true);
    }
  }

  async function handleScraperAgentCall(nextAgentType) {
    setCancelProcess(false);
    setAgentType(nextAgentType);
    // No policies yet? Auto-fetch from the PMS first, then continue as usual
    // (1 policy dispatches directly; >1 opens the policy picker; 0 = the
    // picker's "no policy" state when the PMS also had none).
    const policies = await ensurePolicies(patientPolicies);
    if (policies.length === 1) {
      // Top up from the PMS, then gate on the data the scrape actually needs —
      // a missing member ID / contract # / member DOB is what makes the carrier
      // answer "we couldn't find the claimant" minutes later. The picker path
      // (>1 policy) gets the same check as its own second step.
      const refreshed = await refreshFromPms();
      const current = (refreshed || policies).find(function (p) { return p.id === policies[0].id; }) || policies[0];
      if (!isPolicyComplete(current)) {
        setCompletionPolicy(current);
        setPendingAgentType(nextAgentType);
        return;
      }
      checkPatientInsuranceEligibility(nextAgentType, current.id);
    } else {
      setShowSelectPolicyItemModal(true);
    }
  }

  if (!patientId || !patientForModals) {
    return null;
  }

  return (
    <>
      {!hideTriggers && (
      <div className="treatment-plan-row-insurance-actions" onClick={(e) => e.stopPropagation()}>
        {isFetchedItemSegment ? (
          <Tooltip title={viewIsEstimate ? "View the fetched estimate" : "View the fetched breakdown"}>
            <Button
              className="details-button-color treatment-plan-row-view-button"
              data-onboarding-id={isTourDemo ? "row-view-btn" : undefined}
              onClick={() => {
                setActiveInsuranceTab(viewIsEstimate ? "estimates" : "breakdown");
                setShowInsuranceModal(true);
              }}
            >
              <img src={eyeGrey} alt="" style={{ width: 18, height: 18 }} />
              <span>{viewIsEstimate ? "View Estimate" : "View Breakdown"}</span>
            </Button>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Insurance policy">
              <Button
                className="details-button-color treatment-plan-row-insurance-action-button"
                data-onboarding-id={isTourDemo ? "row-policy-btn" : undefined}
                onClick={() => {
                  setShowPatientInsuranceFormModal(true);
                  setEditPatientInsuranceData(true);
                }}
              >
                <img src={agnetEditIcon} alt="" style={{ width: 18, height: 18 }} />
                <span>Policy</span>
              </Button>
            </Tooltip>
            <Tooltip title="Insurance breakdowns, estimates & claims">
              <Button
                className="details-button-color treatment-plan-row-insurance-action-button"
                data-onboarding-id={isTourDemo ? "row-insurance-btn" : undefined}
                onClick={() => {
                  setActiveInsuranceTab("breakdown");
                  setShowInsuranceModal(true);
                  // Auto-fetch policies from the PMS if this patient has none
                  // yet; the modal shows a spinner in its policy sidebar until
                  // done (isFetchingPolicies).
                  ensurePolicies(patientPolicies);
                }}
              >
                <img src={folder} alt="" style={{ width: 18, height: 18 }} />
                <span>Insurance</span>
              </Button>
            </Tooltip>
          </>
        )}
      </div>
      )}

      {/*
        onPoliciesChanged uses the lightweight refreshPolicies (this row's
        policies only), NOT handleReadData2 — the latter also fires
        onPatientInsuranceUpdated -> getPatientsBySegment (a full patient-list
        refetch) which remounts the row and closes the modal mid-edit.
      */}
      <InsuranceMasterModal
        open={showInsuranceModal}
        setOpen={(v) => {
          setShowInsuranceModal(v);
          if (!v) {
            setDeepLinkPolicyId(undefined); // reset the chip deep-link on close
            if (typeof onInsuranceModalClosed === "function") onInsuranceModalClosed();
          }
        }}
        activeTab={activeInsuranceTab}
        setActiveTab={setActiveInsuranceTab}
        patientPolicies={patientPolicies}
        defaultSelectedPolicyId={deepLinkPolicyId != null ? deepLinkPolicyId : effectiveDefaultPolicyId}
        defaultSelectedEntryId={effectiveDefaultEntryId}
        outcomeSuggestion={outcomeSuggestion}
        patient={patientForModals}
        officeId={officeId}
        hideVerificationTools={hideVerificationTools}
        isFetchingPolicies={isFetchingPolicies}
        agentType={agentType}
        handleScraperAgentCall={handleScraperAgentCall}
        isAgentScrappingData={isAgentScrappingData}
        onPoliciesChanged={refreshPolicies}
        onNoteSaved={handleModalNoteSaved}
        onRequestAddPolicy={() => {
          setPolicyModalInitialMode("add");
          setEditPatientInsuranceData(false);
          setShowPatientInsuranceFormModal(true);
        }}
        breakdownData={breakdownData}
        setLastUploadedBreakdown={setLastUploadedBreakdown}
        isGettingDoctorNote={false}
        checkPatientInsuranceEligibility={checkPatientInsuranceEligibility}
        breakdownEligibilityRefreshTick={breakdownEligibilityRefreshTick}
        breakdownModalEligibilityActive={breakdownModalEligibilityActive}
        setAgentCancelProcess={setCancelProcess}
        eligibilityAgent2FAStatus={agent2FAStatus}
        eligibilityTaskStatus={eligibilityTaskStatus}
        predeterminationData={predeterminationData}
      />

      <PatientPolicyInfoFormModal
        setShowPatientInsuranceFormModal={(open) => {
          setShowPatientInsuranceFormModal(open);
          if (!open) setPolicyModalInitialMode(undefined);
        }}
        checkPatientInsuranceEligibility={checkPatientInsuranceEligibility}
        showPatientInsuranceFormModal={showPatientInsuranceFormModal}
        setEditPatientInsuranceData={setEditPatientInsuranceData}
        editPatientInsuranceData={editPatientInsuranceData}
        patientID={patientId}
        patient={patientForModals}
        setCancelProcess={setCancelProcess}
        handleReadData2={handlePolicySaved}
        initialMode={policyModalInitialMode}
      />

      <Eligibility2FAFormModal
        setShowEligibility2FAFormModal={setShowEligibility2FAFormModal}
        showEligibility2FAFormModal={showEligibility2FAFormModal}
        eligibilityAgentToken={eligibilityAgentToken}
        setCancelProcess={setCancelProcess}
        type="eligibility"
      />

      <EligibilityDataModal
        setShowEligibilityDataModal={setShowEligibilityDataModal}
        showEligibilityDataModal={showEligibilityDataModal}
        eligibilityData={eligibilityData}
        patientPolicies={patientPolicies}
        selectedPolicy={selectedPolicy}
      />

      <SelectPolicyItemModal
        checkPatientInsuranceEligibility={checkPatientInsuranceEligibility}
        setShowPatientInsuranceFormModal={setShowPatientInsuranceFormModal}
        setShowSelectPolicyItemModal={setShowSelectPolicyItemModal}
        showSelectPolicyItemModal={showSelectPolicyItemModal}
        setSelectedPolicy={setSelectedPolicy}
        patientPolicies={patientPolicies}
        selectedPolicy={selectedPolicy}
        setAgentType={setAgentType}
        agentType={agentType}
        patientID={patientId}
        onPoliciesChanged={handleReadData2}
      />

      {/*
        Review modal for auto-fetched policies whose PMS carrier couldn't be
        confidently matched ("review only unknowns"). Opened by ensurePolicies();
        resolving it (save or close) unblocks the awaiting insurance action.
      */}
      <PolicyCompletionModal
        open={!!completionPolicy}
        policy={completionPolicy}
        patientId={patientId}
        onCancel={function () {
          setCompletionPolicy(undefined);
          setPendingAgentType(undefined);
        }}
        onCompleted={async function () {
          const policyId = completionPolicy && completionPolicy.id;
          const agent = pendingAgentType;
          setCompletionPolicy(undefined);
          setPendingAgentType(undefined);
          await refreshPolicies();
          if (policyId && agent) checkPatientInsuranceEligibility(agent, policyId);
        }}
      />

      {reviewProps && <SikkaPolicyReviewModal {...reviewProps} />}
    </>
  );
}
